
import React, { useState, useEffect, useCallback } from 'react';
import Roadmap from '../Roadmap';
import VariousDocuments from './VariousDocuments/VariousDocuments';
import FillingDocuments from './FillingDocuments/FillingDocuments';
import { Participant } from '../step1/types';
import './CreateInvitationStep2.css';
import backButtonIcon from '../../../../../../assests/icons/back-button.png';
import { API_BASE_URL } from '../../../../../../config';
import { DocumentCopyItem } from './VariousDocuments/types';
import {
  findIncompleteEmployerForms,
  formatIncompleteEmployerFormsMessage,
  type FormEmployerContext,
} from '../invitationEmployerFields';

export interface CreateInvitationStep2Props {
  /** Recipients selected in Step 1 */
  selectedParticipants?: Participant[];
  /** Various documents selected in Step 2 */
  selectedDocuments?:string[];
  onDocumentCopyChange?:(checkedDocCodes:string[])=>void;
  /** 記入書類 form codes (forms_to_fill) selected in Step 2 */
  selectedFormCodes?: string[];
  onFormCodesChange?: (formCodes: string[]) => void;
  /** Per-form employer/admin field values before send */
  formEmployerContext?: FormEmployerContext;
  onFormEmployerContextChange?: (formCode: string, fields: Record<string, string>) => void;
  onFormEmployerContextClear?: (formCode: string) => void;
  /** Shown when step-2 validation fails */
  step2ValidationError?: string | null;
  onClearStep2ValidationError?: () => void;
  /** Callback when header back arrow is clicked (go back to Step 1) */
  onBackToStep1?: () => void;
  /** Callback when footer 戻る button is clicked (return to 招待管理) */
  onBack?: () => void;
  /** Callback when save and proceed button is clicked */
  onSaveAndProceed?: () => void;
}
// Shape of each item from GET /api/various-copies (and POST response)
interface VariousCopyRequest {
  docCode: string;
  nameJa: string;
  nameEn?: string;
  nameVi?: string;
  descriptionJa?: string;
  descriptionEn?: string;
  descriptionVi?: string;
}

interface FormToFillApiRow {
  formCode: string;
  nameJa: string;
  nameEn?: string;
  nameVi?: string;
  descriptionJa?: string;
  descriptionEn?: string;
  descriptionVi?: string;
}

const CreateInvitationStep2: React.FC<CreateInvitationStep2Props> = ({
  selectedParticipants = [],
  selectedDocuments = [],
  onDocumentCopyChange,
  selectedFormCodes = [],
  onFormCodesChange,
  formEmployerContext = {},
  onFormEmployerContextChange,
  onFormEmployerContextClear,
  step2ValidationError = null,
  onClearStep2ValidationError,
  onBackToStep1,
  onBack,
  onSaveAndProceed,
}) => {
  const [variousDocumentsOpen, setVariousDocumentsOpen] = useState(true);
  const [fillingDocumentsOpen, setFillingDocumentsOpen] = useState(true);
  const [variousCopyItems, setVariousCopyItems] = useState<DocumentCopyItem[]>([]);
  const [variousCopyLoading, setVariousCopyLoading] = useState(true);
  const [variousCopyError, setVariousCopyError] = useState<string | null>(null);
  const [formToFillItems, setFormToFillItems] = useState<DocumentCopyItem[]>([]);
  const [formsToFillLoading, setFormsToFillLoading] = useState(true);
  const [formsToFillError, setFormsToFillError] = useState<string | null>(null);

  const loadVariousCopies = useCallback(() => {
    setVariousCopyLoading(true);
    setVariousCopyError(null);
    fetch(`${API_BASE_URL}/api/various-copies`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch various copy items');
        }
        return response.json();
      })
      .then((data: VariousCopyRequest[]) => {
        setVariousCopyItems(
          data.map((row) => ({
            id: row.docCode,
            nameJp: row.nameJa,
            nameEn: row.nameEn,
            nameVi: row.nameVi,
            checked: false,
            descriptionJa: row.descriptionJa,
            descriptionEn: row.descriptionEn,
            descriptionVi: row.descriptionVi,
          }))
        );
      })
      .catch((err) => {
        setVariousCopyError(err?.message ?? 'Error loading list');
      })
      .finally(() => {
        setVariousCopyLoading(false);
      });
  }, []);

  useEffect(() => {
    loadVariousCopies();
  }, [loadVariousCopies]);

  const loadFormsToFill = useCallback(() => {
    setFormsToFillLoading(true);
    setFormsToFillError(null);
    fetch(`${API_BASE_URL}/api/forms-to-fill`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch forms to fill');
        }
        return response.json();
      })
      .then((data: FormToFillApiRow[]) => {
        setFormToFillItems(
          data.map((row) => ({
            id: row.formCode,
            nameJp: row.nameJa,
            nameEn: row.nameEn,
            nameVi: row.nameVi,
            checked: false,
            descriptionJa: row.descriptionJa,
            descriptionEn: row.descriptionEn,
            descriptionVi: row.descriptionVi,
          }))
        );
      })
      .catch((err) => {
        setFormsToFillError(err?.message ?? 'Error loading forms');
      })
      .finally(() => {
        setFormsToFillLoading(false);
      });
  }, []);

  useEffect(() => {
    loadFormsToFill();
  }, [loadFormsToFill]);

  const valueForVariousDocuments =
  variousCopyItems.length > 0
    ? variousCopyItems.map((item) => ({
        ...item,
        checked: selectedDocuments.includes(item.id),
      }))
    : undefined;

  const handleDocumentCopyChange = (items: DocumentCopyItem[]) => {
    onDocumentCopyChange?.(items.filter((i) => i.checked).map((i) => i.id));
  };

  const valueForFillingDocuments = !formsToFillLoading
    ? formToFillItems.map((item) => ({
        ...item,
        checked: selectedFormCodes.includes(item.id),
      }))
    : undefined;

  const handleFillingFormChange = (items: DocumentCopyItem[]) => {
    onClearStep2ValidationError?.();
    onFormCodesChange?.(items.filter((i) => i.checked).map((i) => i.id));
  };

  const handleEmployerContextChange = (formCode: string, fields: Record<string, string>) => {
    onClearStep2ValidationError?.();
    onFormEmployerContextChange?.(formCode, fields);
  };

  const incompleteEmployerForms = findIncompleteEmployerForms(
    selectedFormCodes,
    formEmployerContext
  );

  const handleSaveClick = () => {
    onSaveAndProceed?.();
  };

  return (
    <div className="create-invitation-step2">
      {/* Header: back arrow goes to Step 1 */}
      <div className="create-invitation-step2__header">
        <button
          className="create-invitation-step2__back-btn"
          onClick={onBackToStep1}
          aria-label="前のステップへ"
        >
          <img src={backButtonIcon} alt="" className="create-invitation-step2__back-icon" aria-hidden />
        </button>
        <h1 className="create-invitation-step2__title">招待を作成</h1>
      </div>

      {/* Roadmap */}
      <div className="create-invitation-step2__roadmap">
        <Roadmap currentStep={2} />
      </div>

      {/* Grey area with collapsible document sections */}
      <div className="create-invitation-step2__content">
        <VariousDocuments
          isOpen={variousDocumentsOpen}
          onToggle={() => setVariousDocumentsOpen((prev) => !prev)}
          value={valueForVariousDocuments}
          loading={variousCopyLoading}
          error={variousCopyError}
          onChange={handleDocumentCopyChange}
          onDocumentCreated={loadVariousCopies}
          allowCatalogEdit={false}
        />
        <FillingDocuments
          isOpen={fillingDocumentsOpen}
          onToggle={() => setFillingDocumentsOpen((prev) => !prev)}
          value={valueForFillingDocuments}
          loading={formsToFillLoading}
          error={formsToFillError}
          onChange={handleFillingFormChange}
          onFormCatalogChanged={loadFormsToFill}
          employerContext={formEmployerContext}
          onEmployerContextChange={handleEmployerContextChange}
          onEmployerContextClear={onFormEmployerContextClear}
          allowCatalogEdit={false}
        />
      </div>

      {/* Footer */}
      <div className="create-invitation-step2__footer">
        <button type="button" className="create-invitation-step2__back-footer-btn" onClick={onBack}>
        招待へ
        </button>
        <div className="create-invitation-step2__footer-right">
          {step2ValidationError && (
            <p className="create-invitation-step2__validation-error" role="alert">
              {step2ValidationError}
            </p>
          )}
          {incompleteEmployerForms.length > 0 && !step2ValidationError && (
            <p className="create-invitation-step2__validation-hint">
              {formatIncompleteEmployerFormsMessage(incompleteEmployerForms)}
            </p>
          )}
          <button
            type="button"
            className="create-invitation-step2__save-btn"
            onClick={handleSaveClick}
          >
            保存して次へ進む
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateInvitationStep2;
